import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/sv";

import { ShiftPlannerConsultant, ShiftPlannerRow, ShiftPlannerShift } from "./shiftPlanner.types";

dayjs.extend(isoWeek);

export const PLANNER_DATE_FORMAT = "YYYY-MM-DD";

export function weekStartOf(date: string | Date | Dayjs): Dayjs {
	return dayjs(date).startOf("isoWeek");
}

export function weekKeyOf(date: string | Date | Dayjs): string {
	return weekStartOf(date).format(PLANNER_DATE_FORMAT);
}

/** The seven "YYYY-MM-DD" dates of the week starting at `weekStart` (a Monday). */
export function weekDates(weekStart: string): string[] {
	const start = dayjs(weekStart);
	return Array.from({ length: 7 }, (_, i) => start.add(i, "day").format(PLANNER_DATE_FORMAT));
}

/** Whether two shifts on the same date overlap in time (touching boundaries
 * don't). End at or before start wraps past midnight. Cross-date overnight
 * overlap is deliberately ignored — the backend validates that on save. */
export function shiftsOverlap(
	a: Pick<ShiftPlannerShift, "date" | "startTime" | "endTime">,
	b: Pick<ShiftPlannerShift, "date" | "startTime" | "endTime">
): boolean {
	if (a.date !== b.date) return false;
	const minutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
	const spanOf = (shift: { startTime: string; endTime: string }) => {
		const start = minutes(shift.startTime);
		let end = minutes(shift.endTime);
		if (end <= start) end += 24 * 60;
		return { start, end };
	};
	const sa = spanOf(a);
	const sb = spanOf(b);
	return sa.start < sb.end && sb.start < sa.end;
}

/**
 * The first same-consultant time conflict a set of changed shifts would
 * create: each booked shift in `changed` is checked against every OTHER
 * shift of the same consultant in `all` (which should already include the
 * changes). Null when there is no conflict.
 */
export function findConsultantConflict(
	all: ShiftPlannerShift[],
	changed: ShiftPlannerShift[]
): { shift: ShiftPlannerShift; conflicting: ShiftPlannerShift } | null {
	for (const shift of changed) {
		if (!shift.consultantId) continue;
		const conflicting = all.find(
			(other) =>
				other.id !== shift.id &&
				other.consultantId === shift.consultantId &&
				shiftsOverlap(other, shift)
		);
		if (conflicting) return { shift, conflicting };
	}
	return null;
}

/** Scheduled hours including break; end at or before start wraps past midnight. */
export function grossHours(shift: { startTime: string; endTime: string }): number {
	const start = dayjs(`2000-01-01T${shift.startTime}`);
	let end = dayjs(`2000-01-01T${shift.endTime}`);
	if (!end.isAfter(start)) end = end.add(1, "day");
	return end.diff(start, "minute") / 60;
}

/** Worked hours: gross minus break, never negative. */
export function netHours(shift: ShiftPlannerShift): number {
	return Math.max(0, grossHours(shift) - (shift.breakHours || 0));
}

/**
 * The break values the Rast field offers: every half hour, plus 0.75 h (45 min)
 * as the one quarter step. 45 min is a common first-hour break, but there is no
 * demand for 1.75 / 2.75 — the ladder is 0, 0.5, 0.75, 1, 1.5, 2, 2.5 ...
 */
export function nextBreakHours(hours: number): number {
	if (hours < 0.5) return 0.5;
	if (hours < 0.75) return 0.75;
	if (hours < 1) return 1;
	// Half-hour ladder from 1 h: step to the first half hour strictly above.
	return Math.floor(hours * 2) / 2 + 0.5;
}

/** The value below `hours` on the ladder — see {@link nextBreakHours}. */
export function prevBreakHours(hours: number): number {
	if (hours <= 0.5) return 0;
	if (hours <= 0.75) return 0.5;
	if (hours <= 1) return 0.75;
	return Math.ceil(hours * 2) / 2 - 0.5;
}

/**
 * The ladder value closest to a typed number, so a hand-typed 1.75 commits as a
 * value the field could also have stepped to. Ties go to the half hour.
 */
export function snapBreakHours(hours: number): number {
	if (!(hours > 0)) return 0;
	const half = Math.round(hours * 2) / 2;
	return Math.abs(hours - 0.75) < Math.abs(hours - half) ? 0.75 : half;
}

export function dayTotals(shifts: ShiftPlannerShift[]): { hours: number; breakHours: number } {
	return shifts.reduce(
		(acc, shift) => ({
			hours: acc.hours + netHours(shift),
			breakHours: acc.breakHours + (shift.breakHours || 0),
		}),
		{ hours: 0, breakHours: 0 }
	);
}

/** The header summary: distinct days with at least one shift + total worked hours. */
export function plannerSummary(shifts: ShiftPlannerShift[]): { days: number; hours: number } {
	const days = new Set(shifts.map((shift) => shift.date)).size;
	const hours = shifts.reduce((sum, shift) => sum + netHours(shift), 0);
	return { days, hours };
}

/** All week starts (Mondays, "YYYY-MM-DD") from `start` through `end`, inclusive. */
export function weekStartsBetween(start: string, end: string): string[] {
	const weeks: string[] = [];
	for (let week = dayjs(start); !week.isAfter(dayjs(end)); week = week.add(1, "week")) {
		weeks.push(week.format(PLANNER_DATE_FORMAT));
	}
	return weeks;
}

/**
 * The week the planner scrolls to on mount: the week of the job's last shift,
 * or the current week when there are no shifts.
 */
export function plannerFocusWeek(
	shifts: ShiftPlannerShift[],
	today: string | Date | Dayjs = dayjs()
): string {
	const lastDate = shifts.reduce<string | undefined>(
		(last, shift) => (!last || shift.date > last ? shift.date : last),
		undefined
	);
	return weekKeyOf(lastDate ?? today);
}

/**
 * The initial week range of the scrollable planner: every week with a shift
 * plus the current week, padded `padBefore` weeks back and `padAfter` weeks
 * forward. Scrolling extends the range further (see ShiftPlanner).
 */
export function initialWeekRange(
	shifts: ShiftPlannerShift[],
	padBefore: number,
	padAfter: number,
	today: string | Date | Dayjs = dayjs()
): { start: string; end: string } {
	const todayWeek = weekStartOf(today);
	let first = todayWeek;
	let last = todayWeek;
	for (const shift of shifts) {
		const week = weekStartOf(shift.date);
		if (week.isBefore(first)) first = week;
		if (week.isAfter(last)) last = week;
	}
	return {
		start: first.subtract(padBefore, "week").format(PLANNER_DATE_FORMAT),
		end: last.add(padAfter, "week").format(PLANNER_DATE_FORMAT),
	};
}

/**
 * The earliest week the range may extend back to: `padWeeks` weeks before the
 * job's first shift or today, whichever is earlier. Planning happens forward —
 * far-back weeks are dead space — but a month of runway keeps late backdating
 * (a worked shift that was never entered) possible, and a job's own history is
 * always reachable since the floor follows its first shift.
 */
export function plannerRangeFloor(
	shifts: ShiftPlannerShift[],
	padWeeks = 4,
	today: string | Date | Dayjs = dayjs()
): string {
	let earliest = weekStartOf(today);
	for (const shift of shifts) {
		const week = weekStartOf(shift.date);
		if (week.isBefore(earliest)) earliest = week;
	}
	return earliest.subtract(padWeeks, "week").format(PLANNER_DATE_FORMAT);
}

/** The selection/anchor key of one grid cell: `${row.key}:${date}`. Dates make
 * these globally unique, so one selection can span several week blocks. */
export function plannerCellKey(row: ShiftPlannerRow, date: string): string {
	return `${row.key}:${date}`;
}

/** The selection/anchor key of a day-header slot: `header:${date}`. A selected
 * header slot creates a new unassigned shift on that date — the only way to
 * multi-select days in weeks that have no rows. */
export function plannerHeaderKey(date: string): string {
	return `header:${date}`;
}

/** The key of the single "Obemannad" demand row (see buildGlobalRows). */
const UNASSIGNED_ROW_KEY = "unassigned";

/**
 * Builds the GLOBAL rows of the planner — one shared row list rendered by
 * every active week, so a row means the same thing everywhere.
 *
 * The single UNASSIGNED ("Obemannad") row comes FIRST — the open demand sits
 * on top. It is ALWAYS present (it is the demand surface: its cells and the
 * "Lägg till behov" flow create unassigned shifts) and holds EVERY
 * unassigned shift — invitations don't staff a shift, so invited shifts
 * stay here as open demand. A day's shifts stack in the cell, where the
 * week view groups identical times into one "×N" pill. Lanes are gone —
 * demand is just times × count per day.
 *
 * CONSULTANT rows follow, one per consultant BOOKED on any shift, ordered by
 * their earliest shift (ties: roster order). Booking a shift MOVES its pill
 * from the unassigned row to the consultant's row; an open invitation only
 * MIRRORS it as an "Önskad" pill on the rows of ALREADY-BOOKED consultants
 * (the demand-row pill remains, carrying the invited-count badge). An
 * invited-only consultant gets NO row — their invitation lives solely in
 * the demand pill's badge/pending card.
 *
 * Rows span all dates: project a week's view with {@link rowsForWeek}.
 */
export function buildGlobalRows(
	shifts: ShiftPlannerShift[],
	consultants: ShiftPlannerConsultant[]
): ShiftPlannerRow[] {
	function consultantRow(
		consultantShifts: ShiftPlannerShift[],
		consultant: ShiftPlannerConsultant
	): ShiftPlannerRow {
		const row: ShiftPlannerRow = {
			key: `${consultant.id}:0`,
			consultantId: consultant.id,
			name: consultant.name,
			shiftByDate: {},
		};
		// ONE row per consultant — same-day shifts stack inside the cell in
		// start-time order instead of spilling into a duplicate row
		for (const shift of consultantShifts) {
			(row.shiftByDate[shift.date] ??= []).push(shift);
		}
		for (const cell of Object.values(row.shiftByDate)) {
			cell.sort((a, b) => a.startTime.localeCompare(b.startTime));
		}
		return row;
	}

	// A consultant's row holds their booked shifts AND the open shifts they
	// are invited to ("Önskad" pills) — but ONLY consultants with a booked
	// shift get a row at all: an invitation alone doesn't add a consultant to
	// the job, so invited-only consultants show up solely in the demand
	// pill's badge/pending card (an invite round otherwise floods the grid
	// with one near-empty row per asked consultant).
	const shiftsOf = new Map<string, ShiftPlannerShift[]>();
	const add = (id: string, shift: ShiftPlannerShift) => {
		let list = shiftsOf.get(id);
		if (!list) shiftsOf.set(id, (list = []));
		list.push(shift);
	};
	for (const shift of shifts) {
		if (shift.consultantId) add(shift.consultantId, shift);
	}
	for (const shift of shifts) {
		if (shift.consultantId) continue;
		for (const id of shift.invitedConsultantIds ?? []) {
			if (shiftsOf.has(id)) add(id, shift);
		}
	}

	const firstShiftOf = new Map<string, string>();
	for (const [id, list] of shiftsOf) {
		for (const shift of list) {
			const at = `${shift.date}T${shift.startTime}`;
			if (!firstShiftOf.has(id) || at < (firstShiftOf.get(id) as string)) firstShiftOf.set(id, at);
		}
	}
	const rosterIndex = new Map(consultants.map((c, i) => [c.id, i]));
	const orderedIds = [...shiftsOf.keys()].sort((a, b) => {
		const byFirstShift = (firstShiftOf.get(a) as string).localeCompare(
			firstShiftOf.get(b) as string
		);
		if (byFirstShift !== 0) return byFirstShift;
		return (
			(rosterIndex.get(a) ?? Infinity) - (rosterIndex.get(b) ?? Infinity) || a.localeCompare(b)
		);
	});

	const consultantRows = orderedIds.map((id) =>
		consultantRow(
			(shiftsOf.get(id) as ShiftPlannerShift[]).sort((a, b) => a.date.localeCompare(b.date)),
			consultants.find((c) => c.id === id) ?? { id, name: "–" }
		)
	);

	// The demand row holds EVERY unassigned shift — an invitation doesn't
	// staff it, so it stays visible as open demand (with the invited-count
	// badge) while ALSO mirroring as an "Önskad" pill on each invited
	// consultant's row
	const unassignedRow: ShiftPlannerRow = { key: UNASSIGNED_ROW_KEY, shiftByDate: {} };
	for (const shift of shifts) {
		if (shift.consultantId) continue;
		(unassignedRow.shiftByDate[shift.date] ??= []).push(shift);
	}
	for (const cell of Object.values(unassignedRow.shiftByDate)) {
		cell.sort((a, b) => a.startTime.localeCompare(b.startTime));
	}

	return [unassignedRow, ...consultantRows];
}

/** One week's view of the global rows: the same row objects (same key, same
 * consultant/lane identity) narrowed to the week's dates. Consultant rows
 * empty in the week still render — their cells are the append targets — but
 * the "Obemannad" row is dropped when the week holds no unmanned shifts:
 * the day headers already create demand, so the empty row is clutter. */
export function rowsForWeek(globalRows: ShiftPlannerRow[], dates: string[]): ShiftPlannerRow[] {
	return globalRows.flatMap((row) => {
		const shiftByDate: Record<string, ShiftPlannerShift[]> = {};
		for (const date of dates) {
			if (row.shiftByDate[date]) shiftByDate[date] = row.shiftByDate[date];
		}
		if (!row.consultantId && Object.keys(shiftByDate).length === 0) return [];
		return [{ ...row, shiftByDate }];
	});
}

/**
 * One rendered pill in a cell: a group of identical shifts (same times and
 * break) collapsed into "HH:mm - HH:mm ×N". Non-demand cells and shifts with
 * pending applications keep one-shift groups so every badge stays visible. */
export interface ShiftPillGroup {
	shift: ShiftPlannerShift;
	shifts: ShiftPlannerShift[];
}

/**
 * Groups a demand cell's shifts into count pills: identical times + break —
 * and nothing pending — collapse into one "×N" pill ("10-14 ×3"). Shifts
 * with applications or open invitations render individually, so their
 * badges and pending cards stay per shift. Pass `group: false` (consultant
 * rows) to keep every shift its own pill.
 */
export function groupCellShifts(cellShifts: ShiftPlannerShift[], group: boolean): ShiftPillGroup[] {
	if (!group) return cellShifts.map((shift) => ({ shift, shifts: [shift] }));
	const groups = new Map<string, ShiftPlannerShift[]>();
	for (const shift of cellShifts) {
		const pending =
			shift.appliedConsultantNames?.length || shift.invitedConsultantIds?.length
				? `solo:${shift.id}`
				: "";
		const key = `${shift.startTime}|${shift.endTime}|${shift.breakHours ?? ""}|${pending}`;
		let list = groups.get(key);
		if (!list) groups.set(key, (list = []));
		list.push(shift);
	}
	return [...groups.values()].map((shifts) => ({ shift: shifts[0], shifts }));
}

export interface ShiftPlannerDiff {
	created: ShiftPlannerShift[];
	updated: { before: ShiftPlannerShift; after: ShiftPlannerShift }[];
	removed: ShiftPlannerShift[];
}

/** The fields the user can change in the planner; ignores server-owned extras. */
function sameShiftContent(a: ShiftPlannerShift, b: ShiftPlannerShift): boolean {
	return (
		a.date === b.date &&
		a.startTime === b.startTime &&
		a.endTime === b.endTime &&
		(a.breakHours || 0) === (b.breakHours || 0) &&
		a.lane === b.lane &&
		(a.consultantId || undefined) === (b.consultantId || undefined) &&
		sameIdSet(a.invitedConsultantIds, b.invitedConsultantIds)
	);
}

function sameIdSet(a: string[] = [], b: string[] = []): boolean {
	return a.length === b.length && [...a].sort().join() === [...b].sort().join();
}

/**
 * What saving would do: shifts in `current` but not `original` (matched by id)
 * are created, shifts in both with different content are updated, shifts only
 * in `original` are removed. For the parent's save-preview/confirmation UI.
 */
export function diffShifts(
	original: ShiftPlannerShift[],
	current: ShiftPlannerShift[]
): ShiftPlannerDiff {
	const originalById = new Map(original.map((shift) => [shift.id, shift]));
	const currentIds = new Set(current.map((shift) => shift.id));

	const created: ShiftPlannerShift[] = [];
	const updated: ShiftPlannerDiff["updated"] = [];
	for (const shift of current) {
		const before = originalById.get(shift.id);
		if (!before) created.push(shift);
		else if (!sameShiftContent(before, shift)) updated.push({ before, after: shift });
	}

	return { created, updated, removed: original.filter((shift) => !currentIds.has(shift.id)) };
}
